import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { CircleStop, CheckCircle2, XCircle, Flag } from 'lucide-react'

export type EndType = 'default' | 'success' | 'failure' | 'terminate'

export interface EndShapeNodeData {
  label: string
  endType?: EndType
  message?: string
  onDelete?: () => void
  [key: string]: unknown
}

const endConfig: Record<EndType, { icon: typeof CircleStop; color: string; bgColor: string }> = {
  default: { icon: CircleStop, color: '#64748b', bgColor: 'rgba(100, 116, 139, 0.15)' },
  success: { icon: CheckCircle2, color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  failure: { icon: XCircle, color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)' },
  terminate: { icon: Flag, color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
}

interface EndShapeNodeProps {
  data: EndShapeNodeData
  selected?: boolean
}

function EndShapeNodeComponent({ data, selected }: EndShapeNodeProps) {
  const { label, endType = 'default' } = data
  const config = endConfig[endType]
  const Icon = config.icon

  return (
    <div
      className="relative group"
      style={{ width: 120, height: 70 }}
    >
      {/* Rounded pill/oval - classic for terminator nodes */}
      <svg
        viewBox="0 0 120 70"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected
            ? `drop-shadow(0 0 12px ${config.color}50)`
            : 'drop-shadow(0 4px 6px rgba(0,0,0,0.07))',
        }}
      >
        <defs>
          <linearGradient id={`end-grad-${endType}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.08" />
          </linearGradient>
        </defs>
        {/* Oval/pill shape */}
        <rect
          x="3"
          y="3"
          width="114"
          height="64"
          rx="32"
          ry="32"
          fill={`url(#end-grad-${endType})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Double border effect */}
        <rect
          x="8"
          y="8"
          width="104"
          height="54"
          rx="27"
          ry="27"
          fill="none"
          stroke={config.color}
          strokeWidth="1"
          strokeOpacity="0.2"
        />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="p-2 rounded-full mb-1"
          style={{ backgroundColor: config.bgColor }}
        >
          <Icon className="w-4 h-4" style={{ color: config.color }} />
        </div>
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center">
          {label}
        </p>
      </div>

      {/* Target Handle - only input for end nodes */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
        style={{ backgroundColor: config.color }}
      />
    </div>
  )
}

export const EndShapeNode = memo(EndShapeNodeComponent)
export default EndShapeNode
